import { useState, useEffect } from "react";
import { db } from "../firebase/config";
import { doc, getDoc, setDoc, collection, query, where, getDocs } from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import { QRCodeSVG } from "qrcode.react";

// ── Bank / UPI Details ──────────────────────────────────────────────
// Where Dr. Vijay enters the payment details (NEFT + UPI) that get
// shown to societies in the Saas-App when they renew their
// subscription. Read by the saas-app through the getPaymentInfo cloud
// function (functions/paymentInfo.js) — never written by it.
//
// Access: layakarudu@gmail.com always. Any platformUsers user with
// role "SalesHead" can also be granted access, toggled right here in
// this screen (not in User Management) — see firestore.rules for the
// matching real enforcement (client-side checks below are UX only).

const DOC_PATH = ["paymentConfig", "bankDetails"];
const SUPER_ADMIN_EMAIL = "layakarudu@gmail.com";

const S = {
  page: { padding: "16px", maxWidth: "760px", margin: "0 auto", color: "#e8e8f0", boxSizing: "border-box" },
  title: { fontSize: "22px", fontWeight: "700", marginBottom: "4px" },
  sub: { fontSize: "13px", color: "#8888a8", marginBottom: "24px" },
  card: { background: "#1b1d27", border: "1px solid #2e2e40", borderRadius: "12px", padding: "18px", marginBottom: "16px" },
  sectionTitle: { fontSize: "15px", fontWeight: "700", marginBottom: "12px" },
  row: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "10px", marginBottom: "10px", alignItems: "start" },
  label: { fontSize: "12px", color: "#8888a8", display: "block", marginBottom: "4px" },
  input: { width: "100%", padding: "9px 10px", borderRadius: "8px", border: "1px solid #2e2e40", background: "#111217", color: "#fff", fontSize: "14px", boxSizing: "border-box" },
  select: { width: "100%", padding: "9px 10px", borderRadius: "8px", border: "1px solid #2e2e40", background: "#111217", color: "#fff", fontSize: "14px", boxSizing: "border-box" },
  field: { flex: 1 },
  saveBtn: { padding: "12px 20px", borderRadius: "8px", border: "none", background: "#6c63ff", color: "#fff", cursor: "pointer", fontSize: "14px", fontWeight: "700", marginTop: "10px" },
  status: { fontSize: "13px", marginTop: "10px" },
  accessRow: { display: "flex", flexWrap: "wrap", gap: "8px", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid #2e2e40" },
  toggle: { width: "40px", height: "22px", borderRadius: "11px", border: "none", cursor: "pointer", position: "relative" },
  toggleKnob: { width: "18px", height: "18px", borderRadius: "50%", background: "#fff", position: "absolute", top: "2px", transition: "left 0.15s" },
  mismatch: { fontSize: "12px", color: "#ff6b6b", marginTop: "4px" },
};

function emptyForm() {
  return {
    accountHolderName: "",
    bankName: "",
    accountNumber: "",
    ifscCode: "",
    branchName: "",
    accountType: "Savings",
    upiId: "",
  };
}

export default function BankDetailsScreen() {
  const { user } = useAuth();
  const isSuperAdmin = (user?.email || "").trim().toLowerCase() === SUPER_ADMIN_EMAIL;

  const [form, setForm] = useState(emptyForm());
  const [confirmAccountNumber, setConfirmAccountNumber] = useState("");
  const [salesHeadAccess, setSalesHeadAccess] = useState({}); // { uid: true/false }
  const [salesHeads, setSalesHeads] = useState([]); // platformUsers with role SalesHead
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");

  const hasAccess = isSuperAdmin || (user?.role === "SalesHead" && salesHeadAccess[user?.uid] === true);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, ...DOC_PATH));
        if (snap.exists()) {
          const data = snap.data();
          setForm({
            accountHolderName: data.accountHolderName || "",
            bankName: data.bankName || "",
            accountNumber: data.accountNumber || "",
            ifscCode: data.ifscCode || "",
            branchName: data.branchName || "",
            accountType: data.accountType || "Savings",
            upiId: data.upiId || "",
          });
          setConfirmAccountNumber(data.accountNumber || "");
          setSalesHeadAccess(data.salesHeadAccess || {});
        }

        // Only the super admin needs the list of SalesHead users (to manage
        // the toggles) — skip the extra read for everyone else.
        const email = (user?.email || "").trim().toLowerCase();
        if (email === SUPER_ADMIN_EMAIL) {
          const q = query(collection(db, "platformUsers"), where("role", "==", "SalesHead"));
          const usersSnap = await getDocs(q);
          setSalesHeads(usersSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        }
      } catch (err) {
        console.error("Bank details load failed:", err);
        setStatus("Couldn't load current details. " + (err.message || ""));
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  function updateField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function toggleSalesHead(uid) {
    setSalesHeadAccess((prev) => ({ ...prev, [uid]: !prev[uid] }));
  }

  function validate() {
    if (!form.accountHolderName.trim()) return "Account Holder Name is required.";
    if (!form.bankName.trim()) return "Bank Name is required.";
    if (!form.accountNumber.trim()) return "Account Number is required.";
    if (form.accountNumber.trim() !== confirmAccountNumber.trim()) return "Account Number and Confirm Account Number don't match.";
    if (!form.ifscCode.trim()) return "IFSC Code is required.";
    if (!form.branchName.trim()) return "Branch Name is required.";
    if (!form.upiId.trim()) return "UPI ID is required.";
    return "";
  }

  async function handleSave() {
    const err = validate();
    if (err) { setStatus("⚠ " + err); return; }
    setSaving(true);
    setStatus("");
    try {
      const payload = {
        accountHolderName: form.accountHolderName.trim(),
        bankName: form.bankName.trim(),
        accountNumber: form.accountNumber.trim(),
        ifscCode: form.ifscCode.trim().toUpperCase(),
        branchName: form.branchName.trim(),
        accountType: form.accountType,
        upiId: form.upiId.trim(),
        updatedAt: new Date().toISOString(),
      };
      // Only the super admin can change who else has access.
      if (isSuperAdmin) {
        payload.salesHeadAccess = salesHeadAccess;
      }
      await setDoc(doc(db, ...DOC_PATH), payload, { merge: true });
      setStatus("✓ Saved. The app will show these details to societies renewing their subscription.");
    } catch (err) {
      console.error("Bank details save failed:", err);
      setStatus("Couldn't save: " + (err.message || "Unknown error. Check your connection."));
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div style={S.page}>Loading bank details…</div>;
  }

  if (!hasAccess) {
    return (
      <div style={S.page}>
        <div style={S.card}>You do not have permission to view Bank Details.</div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <div style={S.title}>🏦 Bank Details</div>
      <div style={S.sub}>These details are shown to societies in the app when they submit a subscription renewal payment (NEFT or UPI). Changes here apply immediately.</div>

      <div style={S.card}>
        <div style={S.sectionTitle}>Payment Details</div>

        <div style={S.row}>
          <div style={S.field}>
            <label style={S.label}>Account Holder Name</label>
            <input style={S.input} value={form.accountHolderName} onChange={(e) => updateField("accountHolderName", e.target.value)} />
          </div>
        </div>

        <div style={S.row}>
          <div style={S.field}>
            <label style={S.label}>Bank Name</label>
            <input style={S.input} value={form.bankName} onChange={(e) => updateField("bankName", e.target.value)} />
          </div>
          <div style={S.field}>
            <label style={S.label}>Branch Name</label>
            <input style={S.input} value={form.branchName} onChange={(e) => updateField("branchName", e.target.value)} />
          </div>
        </div>

        <div style={S.row}>
          <div style={S.field}>
            <label style={S.label}>Account Number</label>
            <input style={S.input} value={form.accountNumber} maxLength={18} inputMode="numeric" onChange={(e) => updateField("accountNumber", e.target.value.replace(/\D/g, ""))} />
          </div>
          <div style={S.field}>
            <label style={S.label}>Confirm Account Number</label>
            <input style={S.input} value={confirmAccountNumber} maxLength={18} inputMode="numeric" onChange={(e) => setConfirmAccountNumber(e.target.value.replace(/\D/g, ""))} />
            {confirmAccountNumber && form.accountNumber !== confirmAccountNumber && (
              <div style={S.mismatch}>Doesn't match Account Number.</div>
            )}
          </div>
        </div>

        <div style={S.row}>
          <div style={S.field}>
            <label style={S.label}>IFSC Code</label>
            <input style={S.input} value={form.ifscCode} onChange={(e) => updateField("ifscCode", e.target.value.toUpperCase())} />
          </div>
          <div style={S.field}>
            <label style={S.label}>Account Type</label>
            <select style={S.select} value={form.accountType} onChange={(e) => updateField("accountType", e.target.value)}>
              <option value="Savings">Savings</option>
              <option value="Current">Current</option>
            </select>
          </div>
        </div>

        <div style={S.row}>
          <div style={S.field}>
            <label style={S.label}>UPI ID</label>
            <input style={S.input} value={form.upiId} onChange={(e) => updateField("upiId", e.target.value)} placeholder="name@bank" />
          </div>
        </div>

        <button style={S.saveBtn} onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </button>
        {status && <div style={S.status}>{status}</div>}
      </div>

      {form.upiId.trim() && (
        <div style={{ ...S.card, textAlign: "center" }}>
          <div style={S.sectionTitle}>UPI QR Code</div>
          <div style={{ background: "#fff", display: "inline-block", padding: "16px", borderRadius: "8px" }}>
            <QRCodeSVG
              value={`upi://pay?pa=${encodeURIComponent(form.upiId.trim())}&pn=${encodeURIComponent(form.accountHolderName.trim() || "Adyantam Building Circle")}&cu=INR`}
              size={180}
            />
          </div>
          <div style={{ fontSize: "12px", color: "#8888a8", marginTop: "10px" }}>Scans to pay {form.upiId.trim()} — no fixed amount, the payer enters it. This is the same code shown to societies in the app.</div>
        </div>
      )}

      {isSuperAdmin && (
        <div style={S.card}>
          <div style={S.sectionTitle}>Sales Head Access</div>
          <div style={S.sub}>Turn this on for a Sales Head to let them view and edit this screen too. Off by default.</div>
          {salesHeads.length === 0 && <div style={{ color: "#8888a8", fontSize: "13px" }}>No Sales Head users found.</div>}
          {salesHeads.map((sh) => {
            const on = salesHeadAccess[sh.id] === true;
            return (
              <div key={sh.id} style={S.accessRow}>
                <div>
                  <div>{sh.name || sh.email}</div>
                  <div style={{ fontSize: "12px", color: "#8888a8" }}>{sh.email}</div>
                </div>
                <button
                  style={{ ...S.toggle, background: on ? "#6c63ff" : "#333" }}
                  onClick={() => toggleSalesHead(sh.id)}
                  aria-label={`Toggle access for ${sh.email}`}
                >
                  <span style={{ ...S.toggleKnob, left: on ? "20px" : "2px" }} />
                </button>
              </div>
            );
          })}
          <div style={{ fontSize: "12px", color: "#8888a8", marginTop: "10px" }}>Remember to click Save above after changing access.</div>
        </div>
      )}
    </div>
  );
}
